import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Store, ShoppingCart, ChefHat, ArrowRight } from "lucide-react";
import { getPlatformLogo } from "../services/api";

/**
 * eduw.in home: one place to reach every login, so nobody has to remember
 * separate links.
 */
const LOGINS = [
  {
    to: "/admin/login",
    title: "Admin",
    description: "Manage your restaurant, menu, reports and settings",
    Icon: LayoutDashboard,
  },
  {
    to: "/pos/login",
    title: "POS",
    description: "Self-order screen for customers",
    Icon: Store,
    hidden: true, // hidden for now — set to false to show again
  },
  {
    to: "/login",
    title: "Sales",
    description: "Staff POS for taking orders at the counter",
    Icon: ShoppingCart,
  },
  {
    to: "/kitchen/login",
    title: "Kitchen",
    description: "Live orders for the kitchen display",
    Icon: ChefHat,
    hidden: true, // hidden for now — set to false to show again
  },
];

const Landing: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState("");

  // Master admin's welcome image (Platform Settings), else the platform logo.
  useEffect(() => {
    getPlatformLogo()
      .then((res) => setLogoUrl(res.data?.welcomeImage || res.data?.data?.logo || ""))
      .catch(() => setLogoUrl(""));
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-10">
      {logoUrl && (
        <img src={logoUrl} alt="Logo" className="mb-6 h-20 w-auto max-w-[240px] object-contain" />
      )}
      <h1 className="m-0 text-center text-3xl font-bold text-gray-900 sm:text-4xl">Welcome</h1>
      <p className="mt-2 mb-8 text-center text-gray-500">Choose where you want to sign in</p>

      <div className="grid w-full max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2">
        {LOGINS.filter((login) => !login.hidden).map(({ to, title, description, Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 no-underline shadow-sm transition hover:-translate-y-0.5 hover:border-main hover:shadow-md"
          >
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-main text-white">
              <Icon size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="m-0 text-lg font-semibold text-gray-900">{title}</h2>
              <p className="m-0 text-sm text-gray-500">{description}</p>
            </div>
            <ArrowRight
              size={20}
              className="flex-shrink-0 text-gray-400 transition group-hover:translate-x-1 group-hover:text-main"
            />
          </Link>
        ))}
      </div>

      {/* New restaurant owners create their admin account (free trial). */}
      <div className="mt-8 flex flex-col items-center gap-2 text-center">
        <p className="m-0 text-gray-500">New here? Set up your restaurant</p>
        <Link
          to="/admin/signup"
          className="rounded-xl bg-main px-6 py-3 font-semibold text-white no-underline shadow-sm transition hover:bg-main/90"
        >
          Create admin account
        </Link>
      </div>
    </div>
  );
};

export default Landing;
